import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './store/authStore';
import useOrgStore from './store/orgStore';
import Login from './pages/Login';
import AppLayout from './components/layout/AppLayout';
import BoardView from './pages/BoardView';
import SettingsLayout from './pages/SettingsLayout';
import UsersPage from './pages/settings/UsersPage';
import RolesPage from './pages/settings/RolesPage';
import ProfilePage from './pages/settings/ProfilePage';
import ImportPage from './pages/settings/ImportPage';
import OrganizationPage from './pages/settings/OrganizationPage';
import CustomizePage from './pages/settings/CustomizePage';
import TrashPage from './pages/TrashPage';
import PeopleMetricsPage from './pages/PeopleMetricsPage';
import DialogHost from './components/DialogHost';
import BoardProtectionHost from './components/board/BoardProtectionHost';

function ProtectedRoute({ children }) {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const token = useAuthStore((s) => s.token);
  if (token) return <Navigate to="/" replace />;
  return children;
}

function HomePage() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-text-primary mb-2">Selecione um quadro</h2>
        <p className="text-text-secondary text-sm">Escolha um quadro na sidebar para começar</p>
      </div>
    </div>
  );
}

export default function App() {
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const startMePolling = useAuthStore((s) => s.startMePolling);
  const stopMePolling = useAuthStore((s) => s.stopMePolling);
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const fetchOrg = useOrgStore((s) => s.fetchOrg);
  const startOrgPolling = useOrgStore((s) => s.startOrgPolling);
  const stopOrgPolling = useOrgStore((s) => s.stopOrgPolling);
  const org = useOrgStore((s) => s.org);

  useEffect(() => {
    if (token) fetchMe();
  }, [token, fetchMe]);

  useEffect(() => {
    if (token && user?.id) fetchOrg().catch(() => {});
  }, [token, user?.id, fetchOrg]);

  useEffect(() => {
    if (!token || !user?.id) return;
    startMePolling(15000);
    startOrgPolling(5000);
    return () => { stopMePolling(); stopOrgPolling(); };
  }, [token, user?.id, startMePolling, stopMePolling, startOrgPolling, stopOrgPolling]);

  useEffect(() => {
    fetch('/api/v1/public/brand')
      .then((r) => r.json())
      .then(({ data }) => {
        if (!data) return;
        const url = data.favicon_url || data.logo_url;
        if (!url) return;
        let link = document.querySelector('link[rel="icon"]');
        if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
        if (!link.dataset.defaultHref) link.dataset.defaultHref = link.href;
        link.href = url;
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!org) return;
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    if (!link.dataset.defaultHref) link.dataset.defaultHref = link.href;
    link.href = org?.favicon_url || org?.logo_url || link.dataset.defaultHref;
    document.title = 'Arco';
  }, [org?.favicon_url, org?.logo_url, org?.name]);

  return (
    <BrowserRouter>
      <DialogHost />
      <BoardProtectionHost />
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/settings/profile" replace />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="organization" element={<OrganizationPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="import" element={<ImportPage />} />
          <Route path="customize" element={<CustomizePage />} />
        </Route>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="board/:id" element={<BoardView />} />
          <Route path="trash" element={<TrashPage />} />
          <Route path="metrics/people" element={<PeopleMetricsPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
